import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { SENSOR_TYPES, MOCK_SENSOR_VALUES, getStatusColorGradient, getSensorConfigWithPreset } from '../context/SensorConfigContext';
import { useGrow } from '../context/GrowContext';

// All sensors configuration for status bar display
const ALL_SENSORS = [
  { id: 'ph', label: 'pH', category: 'Reservoir' },
  { id: 'ec', label: 'EC', category: 'Reservoir' },
  { id: 'tds', label: 'TDS', category: 'Reservoir' },
  { id: 'waterTemp', label: 'Water', category: 'Reservoir' },
  { id: 'orp', label: 'ORP', category: 'Reservoir' },
  { id: 'airTemp', label: 'Air', category: 'Atmosphere' },
  { id: 'humidity', label: 'RH', category: 'Atmosphere' },
  { id: 'co2', label: 'CO2', category: 'Atmosphere' },
  { id: 'ppfd', label: 'PPFD', category: 'Lighting' },
  { id: 'dli', label: 'DLI', category: 'Lighting' },
];

// Get status color with gradient based on distance from optimal
function getStatusColor(value, config) {
  if (!config) {
    return { bg: '#6b7280', text: '#ffffff', status: 'unknown' };
  }

  const { min, max, optimalMin, optimalMax } = config;
  const { color, status } = getStatusColorGradient(value, min, max, optimalMin, optimalMax);

  return { bg: color, text: '#ffffff', status };
}

function StatusItem({ sensorId, label, category, presetOptimal }) {
  const value = MOCK_SENSOR_VALUES[sensorId];
  const config = getSensorConfigWithPreset(sensorId, presetOptimal) || SENSOR_TYPES[sensorId] || {};
  const { bg, text, status } = getStatusColor(value, config);

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {label} ({category})
      </Typography>
      <br />
      <Typography variant="caption">
        Current: {value} {config.unit || ''}
      </Typography>
      <br />
      <Typography variant="caption">
        Optimal: {config.optimalMin}-{config.optimalMax} {config.unit || ''}
      </Typography>
      <br />
      <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
        Status: {status}
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderRadius: 1,
          bgcolor: bg,
          color: text,
          minWidth: 60,
          cursor: 'pointer',
          transition: 'transform 0.2s',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.65rem',
            fontWeight: 500,
            opacity: 0.9,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.8rem',
            fontWeight: 700,
          }}
        >
          {typeof value === 'number' ? value.toFixed(1) : value}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.55rem',
            opacity: 0.8,
          }}
        >
          {config.unit || ''}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function SensorStatusBar({ configuredSensors = [] }) {
  const { getOptimalRange } = useGrow();

  // Filter sensors based on configuredSensors if provided
  const sensorsToShow = React.useMemo(() => {
    if (configuredSensors && configuredSensors.length > 0) {
      return ALL_SENSORS.filter(sensor => configuredSensors.includes(sensor.id));
    }
    return ALL_SENSORS;
  }, [configuredSensors]);

  // Group sensors by category
  const categories = React.useMemo(() => {
    const grouped = {};
    sensorsToShow.forEach(sensor => {
      if (!grouped[sensor.category]) {
        grouped[sensor.category] = [];
      }
      grouped[sensor.category].push(sensor);
    });
    return grouped;
  }, [sensorsToShow]);

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          color: 'text.secondary',
        }}
      >
        Current Readings
      </Typography>

      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {Object.entries(categories).map(([category, sensors]) => (
          <Box key={category}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.65rem',
                color: 'text.secondary',
                mb: 0.5,
                display: 'block',
              }}
            >
              {category}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {sensors.map(sensor => (
                <StatusItem
                  key={sensor.id}
                  sensorId={sensor.id}
                  label={sensor.label}
                  category={sensor.category}
                  presetOptimal={getOptimalRange(sensor.id)}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      {/* Legend */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
        justifyContent="center"
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#22c55e' }} />
          <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.65rem' }}>
            Optimal
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#eab308' }} />
          <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.65rem' }}>
            Warning (0-10%)
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#f97316' }} />
          <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.65rem' }}>
            Caution (10-20%)
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#ef4444' }} />
          <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.65rem' }}>
            Critical (20%+)
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
}
